import { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardHeader } from '@/components/ui/Card';
import { useTenant } from '@/contexts/TenantContext';
import { useLanguage } from '@/hooks/useLanguage';
import {
  DEFAULT_REPORT_EMAIL,
  DEFAULT_REPORT_SEND_DAY,
  DEFAULT_REPORT_SEND_TIME,
  MAX_EMAIL_LENGTH,
  MAX_REPORT_EMAILS,
  loadReportPrefs,
  normalizeEmail,
  normalizeSendTime,
  saveReportPrefs,
  validateEmailInput,
} from '@/lib/reports/reportPrefs';
import { Mail, Loader2, Plus, Trash2, Check, Clock } from 'lucide-react';

const DAY_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 0, label: 'Sunday' },
];

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = [0, 15, 30, 45] as const;

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function formatSlot(hour: number, minute: number): string {
  return `${pad2(hour)}:${pad2(minute)}`;
}

function parseSlot(time: string): { hour: number; minute: number } {
  const normalized = normalizeSendTime(time) || DEFAULT_REPORT_SEND_TIME;
  const [hh, mm] = normalized.split(':').map(Number);
  const minute = (MINUTES as readonly number[]).includes(mm) ? mm : 0;
  return { hour: Math.min(23, Math.max(0, hh)), minute };
}

export function ReportEmailSettings() {
  const { t } = useLanguage();
  const { sheet } = useTenant();
  const meterId = sheet.id || import.meta.env.VITE_CLIENT_ID || '';

  const [emails, setEmails] = useState<string[]>([DEFAULT_REPORT_EMAIL]);
  const [draft, setDraft] = useState('');
  const [sendDay, setSendDay] = useState(DEFAULT_REPORT_SEND_DAY);
  const [sendTime, setSendTime] = useState(DEFAULT_REPORT_SEND_TIME);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [timeOpen, setTimeOpen] = useState(false);
  const hourListRef = useRef<HTMLDivElement>(null);
  const timePickerRef = useRef<HTMLDivElement>(null);

  // Close picker on outside click
  useEffect(() => {
    if (!timeOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!timePickerRef.current?.contains(e.target as Node)) setTimeOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [timeOpen]);

  const { hour, minute } = useMemo(() => parseSlot(sendTime), [sendTime]);

  // When picker opens, scroll hour list to the selected hour.
  useEffect(() => {
    if (!timeOpen) return;
    const root = hourListRef.current;
    if (!root) return;
    requestAnimationFrame(() => {
      const selected = root.querySelector<HTMLElement>(`[data-hour="${hour}"]`);
      selected?.scrollIntoView({ block: 'center' });
    });
  }, [timeOpen, hour]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!meterId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError('');
      setSuccess('');
      try {
        const prefs = await loadReportPrefs(meterId);
        if (cancelled) return;
        if (prefs) {
          setEmails(prefs.emails.length ? prefs.emails : [DEFAULT_REPORT_EMAIL]);
          setSendDay(prefs.sendDay);
          setSendTime(prefs.sendTime);
        } else {
          setEmails([DEFAULT_REPORT_EMAIL]);
          setSendDay(DEFAULT_REPORT_SEND_DAY);
          setSendTime(DEFAULT_REPORT_SEND_TIME);
        }
      } catch (err: unknown) {
        console.error(err);
        if (cancelled) return;
        const code = typeof err === 'object' && err && 'code' in err
          ? String((err as { code?: string }).code || '')
          : '';
        if (code.includes('permission-denied')) {
          setError('Firestore blocked report_prefs. Deploy firestore rules once, then refresh.');
        } else {
          setError('Failed to load report email settings. Check console for details.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [meterId]);

  const handleDraftChange = (value: string) => {
    const cleaned = value.replace(/[\s,;]+/g, '').slice(0, MAX_EMAIL_LENGTH);
    setDraft(cleaned);
    if (error && !error.startsWith('Firestore')) setError('');
    if (success) setSuccess('');
  };

  const handleAddEmail = () => {
    setError('');
    setSuccess('');
    const validationError = validateEmailInput(draft);
    if (validationError) {
      setError(validationError);
      return;
    }
    const email = normalizeEmail(draft);
    if (emails.includes(email)) {
      setError('That email is already added.');
      return;
    }
    if (emails.length >= MAX_REPORT_EMAILS) {
      setError(`You can add at most ${MAX_REPORT_EMAILS} emails per meter.`);
      return;
    }
    setEmails(prev => [...prev, email]);
    setDraft('');
  };

  const handleRemoveEmail = (email: string) => {
    setError('');
    setSuccess('');
    setEmails(prev => prev.filter(e => e !== email));
  };

  const setHour = (h: number) => {
    setSendTime(formatSlot(h, minute));
  };

  const setMinute = (m: number) => {
    setSendTime(formatSlot(hour, m));
  };

  const handleSave = async () => {
    if (!meterId) {
      setError('No meter selected.');
      return;
    }
    setError('');
    setSuccess('');

    const pending = draft.trim() ? normalizeEmail(draft) : '';
    let nextEmails = [...emails];
    if (pending) {
      const validationError = validateEmailInput(pending);
      if (validationError) {
        setError(validationError);
        return;
      }
      if (!nextEmails.includes(pending)) {
        if (nextEmails.length >= MAX_REPORT_EMAILS) {
          setError(`You can add at most ${MAX_REPORT_EMAILS} emails per meter.`);
          return;
        }
        nextEmails.push(pending);
      }
      setDraft('');
      setEmails(nextEmails);
    }

    if (nextEmails.length === 0) {
      setError('Add at least one email address.');
      return;
    }
    for (const email of nextEmails) {
      const validationError = validateEmailInput(email);
      if (validationError) {
        setError(`${email}: ${validationError}`);
        return;
      }
    }

    const time = normalizeSendTime(sendTime);
    if (!time) {
      setError('Enter a valid time (HH:MM).');
      return;
    }

    setSaving(true);
    try {
      const saved = await saveReportPrefs({
        meterId,
        emails: nextEmails,
        sendDay,
        sendTime: time,
      });
      setEmails(saved.emails);
      setSendDay(saved.sendDay);
      setSendTime(saved.sendTime);
      setSuccess('Report email settings saved. Changes apply without redeploy.');
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader
        eyebrow={t('Anomaly Report')}
        title={t('Report Email Recipients')}
        description={`${t('Meter')}: ${sheet.label || meterId}`}
      />
      {error && (
        <div className="mb-3 text-xs font-semibold text-red-600 bg-red-50 p-2 rounded-lg border border-red-200">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-3 text-xs font-semibold text-emerald-700 bg-emerald-50 p-2 rounded-lg border border-emerald-200">
          {success}
        </div>
      )}

      {loading ? (
        <div className="text-xs text-ink-400 flex items-center gap-2 py-2">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading email settings...
        </div>
      ) : (
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-ink-400">
              {t('Mail is')}
            </div>
            {emails.length === 0 ? (
              <div className="text-xs text-ink-400 italic">No emails added yet.</div>
            ) : (
              emails.map(email => (
                <div
                  key={email}
                  className="flex items-center justify-between bg-ink-50 p-2 rounded-lg border border-ink-100"
                >
                  <div className="flex items-center gap-2 text-sm font-semibold text-ink-700 min-w-0">
                    <Mail className="w-4 h-4 text-brand-500 shrink-0" />
                    <span className="truncate">{email}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveEmail(email)}
                    className="text-red-400 hover:text-red-600 p-1 rounded hover:bg-red-50 transition-colors shrink-0"
                    title="Remove email"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))
            )}

            <div className="flex gap-2">
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                spellCheck={false}
                maxLength={MAX_EMAIL_LENGTH}
                placeholder="name@gmail.com"
                value={draft}
                onChange={e => handleDraftChange(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddEmail();
                  }
                }}
                className="flex-1 rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
              <button
                type="button"
                onClick={handleAddEmail}
                disabled={!draft.trim() || emails.length >= MAX_REPORT_EMAILS}
                className="rounded-lg bg-brand-600 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 shrink-0"
              >
                <Plus className="w-4 h-4" />
                {t('Add more')}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 items-end">
            <label className="block space-y-1.5 min-w-0">
              <span className="h-4 flex items-center text-[10px] font-semibold uppercase tracking-wide text-ink-400">
                {t('Receive day')}
              </span>
              <select
                value={sendDay}
                onChange={e => setSendDay(Number(e.target.value))}
                className="w-full h-10 rounded-lg border border-ink-200 px-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 bg-white"
              >
                {DAY_OPTIONS.map(d => (
                  <option key={d.value} value={d.value}>{t(d.label)}</option>
                ))}
              </select>
            </label>

            <div className="block space-y-1.5 min-w-0 relative" ref={timePickerRef}>
              <span className="h-4 flex items-center text-[10px] font-semibold uppercase tracking-wide text-ink-400">
                {t('Receive time (IST)')}
              </span>
              <button
                type="button"
                onClick={() => setTimeOpen(o => !o)}
                className="w-full h-10 rounded-lg border border-ink-200 px-3 text-sm tabular-nums bg-white flex items-center justify-between gap-2 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              >
                <span className="font-medium text-ink-800">{formatSlot(hour, minute)}</span>
                <Clock className="w-4 h-4 text-ink-400 shrink-0" />
              </button>

              {timeOpen && (
                <div className="absolute z-20 top-[calc(100%+4px)] left-0 right-0 rounded-lg border border-ink-200 bg-white shadow-lg overflow-hidden">
                  <div className="grid grid-cols-2 divide-x divide-ink-100">
                    <div
                      ref={hourListRef}
                      className="max-h-40 overflow-y-auto overscroll-contain py-1"
                    >
                      {HOURS.map(h => {
                        const selected = hour === h;
                        return (
                          <button
                            key={h}
                            type="button"
                            data-hour={h}
                            onClick={() => setHour(h)}
                            className={[
                              'w-full px-3 py-1.5 text-sm text-center tabular-nums transition-colors',
                              selected
                                ? 'bg-brand-600 text-white font-semibold'
                                : 'text-ink-800 hover:bg-ink-50',
                            ].join(' ')}
                          >
                            {pad2(h)}
                          </button>
                        );
                      })}
                    </div>
                    <div className="max-h-40 overflow-y-auto overscroll-contain py-1">
                      {MINUTES.map(m => {
                        const selected = minute === m;
                        return (
                          <button
                            key={m}
                            type="button"
                            onClick={() => {
                              setMinute(m);
                              setTimeOpen(false);
                            }}
                            className={[
                              'w-full px-3 py-1.5 text-sm text-center tabular-nums transition-colors',
                              selected
                                ? 'bg-brand-600 text-white font-semibold'
                                : 'text-ink-800 hover:bg-ink-50',
                            ].join(' ')}
                          >
                            {pad2(m)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <p className="text-[10px] text-ink-400 -mt-2">
            {t('15-minute IST slots. If this week’s slot already passed, mail goes out next week.')}
          </p>

          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving}
            className="w-full rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Check className="w-4 h-4" />
                {t('Save report email settings')}
              </>
            )}
          </button>
        </div>
      )}
    </Card>
  );
}
